
import { useEffect, useState, useMemo } from 'react';
import {
  SalesOrderInfo,
  fetchSalesOrders,
  selectSalesOrders,
  selectServicesTranslations,
  fetchServicesTranslations,
} from '../../../store/slices/salesOrders';
import { useAppDispatch, useAppSelector } from '../../../store/store';

import { useConsultationContext } from '../../../context/ConsultationContext';
import { IS_CONSULTATION, IS_TEST } from '../../../enums/enums';
import { getFullNameInitials } from '../../../helpers';
import {
  IAccountInfo,
  IPerson,
  selectAccountInfo,
  selectPersonLoggedIn,
} from '../../../store/slices/auth';
import moment from 'moment';
import styles from './AppointmentList.module.scss';
import { IDoctor, selectDoctors } from '../../../store/slices/doctors';
import { useTranslation } from '../../../hooks/useTranslations';
import { buildTranslatedServicesMap, getTranslatedServicesName } from '../../../utils/miscUtils';

export default function AppointmentList() {
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();
  const salesOrders: SalesOrderInfo[] = useAppSelector(selectSalesOrders);
  const { selectedListOrder, setSelectedListOrder } = useConsultationContext();
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const doctors: IDoctor[] = useAppSelector<IDoctor[]>(selectDoctors);
  const personLoggedIn = useAppSelector<IPerson | undefined>(selectPersonLoggedIn);
  const servicesTranslations = useAppSelector(selectServicesTranslations);

  const lang = (localStorage.getItem('lang') || 'en').toLowerCase().trim();

  const loadedLocales = moment.locales(); 
  const localeToUse = loadedLocales.includes(lang) ? lang : 'en';
  moment.locale(localeToUse);
  
  
  useEffect(() => {
    dispatch(fetchSalesOrders());
    dispatch(fetchServicesTranslations());
  }, [dispatch]);


  const translatedServicesMap = useMemo(
  () => buildTranslatedServicesMap(servicesTranslations, lang),
  [servicesTranslations, lang]
);


  const [salesOrderType, setSalesOrderType] = useState<
    | 'all'
    | 'Test Appointments'
    | 'Test without appointments'
    | 'Consultation Appointments'
    | 'Consultation without appointments'
  >('all');

  const onChangeSalesOrderTypeHandler = (e: any) => {
    setSalesOrderType(e.target.value);
  };

  const displaySalesOrders = () => {
    const doctorLoggedIn = doctors.find(
      (doctor) => parseInt(doctor.person_id) === Number(personLoggedIn?.person_id)
    );

    const filteredOrders = salesOrders.filter((appointments) =>
      doctorLoggedIn?.person_appointments.find(
       (docAppintmnet) =>
          Number(docAppintmnet.sales_order_id) === appointments.sales_order_item.sales_order_id &&
          Number(docAppintmnet.sales_order_item_id) ===
            appointments.sales_order_item.sales_order_item_id
      )
    );

    const salesOrderToShow = doctorLoggedIn ? filteredOrders : salesOrders;

    const filteredSalesOrdersByService = salesOrderToShow.filter((salesOrder) => {
       // if location doesn't serve consultations then filter them out

      if (
        !accountInfo.serves_consultations &&
        IS_CONSULTATION.includes(salesOrder.service_service_group.service_group_id)
      )
        return false;
              // if location doesn't serve diagnostics then filter them out
      if (
        !accountInfo.serves_diagnostics &&
        IS_TEST.includes(salesOrder.service_service_group.service_group_id)
      )
        return false;
      return true;
    });

    if (salesOrderType === 'all') {
      const walkinAppointments = filteredSalesOrdersByService.filter((upcomings) => {
        const isFinalizedOrParked =
          upcomings?.work_order?.work_order_status_id === 4 ||
          upcomings?.work_order?.work_order_status_id === 2;
        return !upcomings.sales_order_appointment.start && !isFinalizedOrParked;
      });

      const dateAppointments = filteredSalesOrdersByService.filter((upcomings) => {
        const isFinalizedOrParked =
          upcomings?.work_order?.work_order_status_id === 4 ||
          upcomings?.work_order?.work_order_status_id === 2;
        return upcomings.sales_order_appointment.start && !isFinalizedOrParked;
      });

      const sortedDateAppointments = dateAppointments.sort((a: any, b: any) => {
     const aStartDate: any = new Date(a.sales_order_appointment.start);
        const bStartDate: any = new Date(b.sales_order_appointment.start);
        const currentDate = new Date();
        if (aStartDate > currentDate && bStartDate > currentDate) {
          return aStartDate - bStartDate;
        } else if (aStartDate > currentDate) {
          return -1;
        } else if (bStartDate > currentDate) {
          return 1;
        } else {
          return bStartDate - aStartDate;
        }
      });

      return [...walkinAppointments, ...sortedDateAppointments];
    }

    return filteredSalesOrdersByService?.filter((salesOrder) => {
      const isTest = IS_TEST.includes(salesOrder.service_service_group.service_group_id);
      const isConsultation = IS_CONSULTATION.includes(
        salesOrder.service_service_group.service_group_id
      );
      const isFinalizedOrParked =
        salesOrder?.work_order?.work_order_status_id === 4 ||
        salesOrder?.work_order?.work_order_status_id === 2;

      if (salesOrderType === 'Test Appointments') {
        return isTest && salesOrder.sales_order_appointment.start;
      }
      if (salesOrderType === 'Test without appointments') {
        return isTest && !salesOrder.sales_order_appointment.start && !salesOrder.work_order;
      }
      if (salesOrderType === 'Consultation Appointments') {
        return isConsultation && salesOrder.sales_order_appointment.start && !isFinalizedOrParked;
      }
      return isConsultation && !salesOrder.sales_order_appointment.start && !isFinalizedOrParked;
    });
  };

  const filteredSalesOrders =
    salesOrderType === 'Test Appointments' || salesOrderType === 'Consultation Appointments'
      ? displaySalesOrders().sort((a: any, b: any) => {
        const aStartDate: any = new Date(a.sales_order_appointment.start);
          const bStartDate: any = new Date(b.sales_order_appointment.start);
          const currentDate = new Date();
          if (aStartDate > currentDate && bStartDate > currentDate) {
            return aStartDate - bStartDate;
          } else if (aStartDate > currentDate) {
            return -1;
          } else if (bStartDate > currentDate) {
            return 1;
          } else {
            return bStartDate - aStartDate;
          }
        })
      : displaySalesOrders();

  return (
    <div className={styles.mainContainer}>
      <div className={styles.upcomingsContainer}>
        <div className={styles.upcomings}>{translations('video.consulting.upcoming.title')}</div>
        <select
          value={salesOrderType}
          onChange={onChangeSalesOrderTypeHandler}
          className={styles.selectOption}
        >
          <option value="all">{translations('all')}</option>
          {accountInfo.serves_diagnostics && (
            <>
              <option value="Test Appointments">{translations('test.appointment')}</option>
              <option value="Test without appointments">{translations('test.ohne.termin')}</option>
            </>
          )}
          {accountInfo.serves_consultations && (
            <>
              <option value="Consultation Appointments">
                {translations('consultation.appointment')}
              </option>
              <option value="Consultation without appointments">
                {translations('beratung.ohne.termin')}
              </option>
            </>
          )}
        </select>
      </div>

      {filteredSalesOrders.map((data, idx) => {
        const lessThanOneHour = data.sales_order_appointment.start
          ? new Date(data.sales_order_appointment.start).getTime() > new Date().getTime() &&
            new Date(data.sales_order_appointment.start).getTime() - new Date().getTime() < 3600000
          : false;

        const isConsultation = IS_CONSULTATION.includes(
          data.service_service_group.service_group_id
        );

        const isSelected =
          selectedListOrder &&
          selectedListOrder.sales_order_item.sales_order_id ===
            data.sales_order_item.sales_order_id &&
          selectedListOrder.sales_order_item.sales_order_item_id ===
            data.sales_order_item.sales_order_item_id;

        const navigateToOrder = () => {
          setSelectedListOrder(data);
        };
        const doctorAssignedFullName = (data: SalesOrderInfo) => {
          const tempoDoctorAssigned = doctors.find((doctor) =>
            doctor.person_appointments.find((appointment) => {
              if (
                data?.sales_order_item.sales_order_id &&
                data?.sales_order_item.sales_order_item_id
              ) {
                return (
                  appointment.sales_order_id == data.sales_order_item.sales_order_id &&
                  appointment.sales_order_item_id == data.sales_order_item.sales_order_item_id
                );
              }
            })
          );
          if (tempoDoctorAssigned) {
            return `${tempoDoctorAssigned.person_firstname} ${tempoDoctorAssigned.person_lastname}`;
          }
          return undefined;
        };

        return (
          <div
            key={idx}
            onClick={navigateToOrder}
            className={styles.container}
            style={isSelected ? { background: '#F4F7FD' } : {}}
          >
            <div
              className={
                !isConsultation
                  ? `${styles.avatar} ${styles.yellow}`
                  : !doctorAssignedFullName(data)
                  ? `${styles.avatar} ${styles.gray}`
                  : `${styles.avatar} ${styles.blue}`
              }
            >
              {getFullNameInitials(
                `${data.customer_person.person_firstname} ${data.customer_person.person_lastname}`
              )}
              <div
                className={styles.dot}
                style={
                  !isConsultation
                    ? { backgroundColor: '#EFE882' }
                    : !doctorAssignedFullName(data)
                    ? { backgroundColor: 'rgba(229, 229, 229, 1)' }
                    : {}
                }
              />
            </div>

            <div className={styles.itemContainer}>
              <div className={styles.name}>
                {data.customer_person.person_firstname} {data.customer_person.person_lastname}
              </div>

              <div className={styles.issue}>
                {getTranslatedServicesName(
                  data.service.service_id,
                 translatedServicesMap
                )}
              </div>

              {isConsultation && (
                <div className={styles.doctor}>
                  {doctorAssignedFullName(data)
                    ? translations('label.assigned') + ' ' + doctorAssignedFullName(data)
                    : translations('label.unassigned_doctor')}
                </div>
              )}

              <div className={styles.date}>
                {data.sales_order_appointment.start
                  ? moment(data.sales_order_appointment.start).format('DD.MM.YYYY  |  HH:mm -')
                  : translations('walkin_appointment')}
                {data.sales_order_appointment.start
                  ? moment(data.sales_order_appointment.end_time).format(' HH:mm')
                  : ''}
              </div>

              {data.sales_order_appointment.start && (
                <div
                  className={styles.timeLeft}
                  style={lessThanOneHour ? { color: '#FC5E4D' } : {}}
                >
                  {moment(data.sales_order_appointment.start).fromNow()}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
